/**
 * Playback state for synchronized content
 */
export enum SyncObjectPlayState {
  /** Content is stopped (no playback) */
  Idle = 'IDLE',
  /** Content is currently playing */
  Playing = 'PLAYING',
}

/**
 * System/server availability status
 */
export enum SystemObjectActiveState {
  /** Server is ready and operational */
  Active = 'active',
  /** Server is unavailable (maintenance, offline, etc.) */
  Inactive = 'inactive',
}

/**
 * Represents the synchronized state of an interactive content item
 *
 * This is the core object that tracks what should be playing where and when.
 * It describes the current playback state for a specific piece of content
 * in a specific interactive session.
 */
export type SyncObject = {
  /** Unique identifier for the content (track/audio/video ID) */
  content_id: string
  /** Unique identifier for this interactive session/room */
  interactive_id: string
  /** Server timestamp when the play event occurred (milliseconds since epoch) */
  event_time: number
  /** Sequence number for ordering events (increments with each change) */
  seq: number
  /** Type of object ('sync', 'change', etc.) */
  type: string
  /** Current playback state (playing or idle) */
  play_state: SyncObjectPlayState
  /** How much time has elapsed since the event_time (milliseconds) */
  elapsed_time: number
}

/**
 * Complete state snapshot sent when client first connects
 *
 * This provides a full picture of all content that's currently playing
 * across all interactive sessions. Used to "catch up" a new client to
 * the current state of the system.
 */
export type StateObject = {
  /** Array of all currently active sync objects */
  state: Array<SyncObject>
  /** Overall sequence number for this state update */
  seq: number
  /** System/server status information */
  system: SystemObject
  /** Message type identifier */
  type: 'state'
}

/**
 * Incremental state change event
 *
 * Sent whenever playback state changes (play, pause, track switch).
 * More efficient than StateObject for ongoing updates since it only
 * includes the changed content, not the entire state.
 */
export type ChangeObject = SyncObject & {
  /** Message type identifier */
  type: 'change'
}

/**
 * System/server status message
 *
 * Notifies clients about server availability and status changes.
 * Used to show connection issues, maintenance mode, etc.
 */
export type SystemObject = {
  /** Message type identifier */
  type: 'system'
  /** Optional sequence number */
  seq?: number
  /** Server availability status */
  state: SystemObjectActiveState
  /** Human-readable status message */
  message: string
}

/**
 * Type guard to check if input is a StateObject
 *
 * StateObject has a 'state' array field, while ChangeObject does not.
 * Used in message routing to determine message type at runtime.
 *
 * @param input - Message to check (ChangeObject or StateObject)
 * @returns True if input is a StateObject
 */
export function isStateObject(
  input: ChangeObject | StateObject,
): input is StateObject {
  return (input as StateObject).state instanceof Array
}
